import React from 'react';
import { Stack, Typography } from '@mui/material';
import type { TopOpsDocument } from '../../api/services/document.service';
import DataTable, { Column } from '../ui/DataTable';
import { EmptyState } from '../ui/States';
import { FileGlyph, StatusDot } from './vaultUI';
import { describeExpiry, categoryLabel, toneForDocument } from './vaultStatus';

// The file list, in the shape a files app uses: sortable columns, a
// selection that drives the inspector, and a double click that opens. It is
// the site's one table (DataTable) with the vault's columns. Sorting
// happens client-side over the page you are looking at — the API already
// ranks by upload date (newest first, the order rows arrive in), and re-querying on every header click would make
// the list feel slower than the folder it is imitating.

interface Props {
  documents: TopOpsDocument[];
  selectedId?: string | null;
  onSelect?: (document: TopOpsDocument) => void;
  onOpen?: (document: TopOpsDocument) => void;
  showCategory?: boolean;
  emptyMessage?: string;
}

const STATUS_ORDER: Record<string, number> = { expired: 0, expiring_soon: 1, current: 2, archived: 3 };
const STATUS_LABEL: Record<string, string> = {
  expiring_soon: 'Expiring', archived: 'Superseded', expired: 'Expired', current: 'Current',
};
const time = (iso?: string | null) => (iso ? new Date(iso).getTime() : null);

const FileTable: React.FC<Props> = ({
  documents, selectedId, onSelect, onOpen, showCategory, emptyMessage = 'Nothing here yet.',
}) => {
  const columns: Column<TopOpsDocument>[] = [
    {
      key: 'fileName',
      header: 'Name',
      width: 220,
      sortValue: (d) => d.fileName,
      render: (d) => (
        <Stack direction="row" gap={1.25} alignItems="center" sx={{ minWidth: 0 }}>
          <FileGlyph contentType={d.file?.contentType} />
          <Typography variant="body2">{d.fileName}</Typography>
        </Stack>
      ),
    },
    // Owner and category go on a phone: the filename already carries whose
    // document it is, and Status and Expires are what somebody checking a
    // vault from a phone came for.
    {
      key: 'ownerLabel',
      header: 'Filed against',
      width: 150,
      hide: 'phone',
      sortValue: (d) => d.ownerLabel || 'Company',
      render: (d) => <Typography variant="body2">{d.ownerLabel || 'Company'}</Typography>,
    },
    ...(showCategory ? [{
      key: 'category',
      header: 'Category',
      width: 110,
      hide: 'tablet' as const,
      sortValue: (d: TopOpsDocument) => categoryLabel(d.category),
      render: (d: TopOpsDocument) => <Typography variant="body2" color="text.secondary">{categoryLabel(d.category)}</Typography>,
    }] : []),
    {
      key: 'status',
      header: 'Status',
      sortValue: (d) => STATUS_ORDER[d.status] ?? 9,
      render: (d) => <StatusDot tone={toneForDocument(d.status)} label={STATUS_LABEL[d.status] || 'Current'} />,
    },
    {
      key: 'expiresAt',
      header: 'Expires',
      // Documents with no expiry sort last whichever way the column points
      // — "never expires" is not the earliest date, it is not a date at all.
      sortValue: (d) => time(d.expiresAt),
      render: (d) => (
        <Typography variant="body2" sx={{ color: d.status === 'expired' ? 'error.main' : 'text.secondary' }}>
          {describeExpiry(d.expiresAt) || 'Never'}
        </Typography>
      ),
    },
  ];

  return (
    <DataTable
      columns={columns}
      rows={documents}
      rowKey={(d) => d._id}
      selectedKey={selectedId}
      onRowClick={onSelect}
      onRowDoubleClick={onOpen}
      empty={<EmptyState title={emptyMessage} compact />}
    />
  );
};

export default FileTable;
